import Draggable from "react-draggable";
import { BaseModalHeader } from "./BaseModalHeader.jsx";
import { BaseModalFooter } from "./BaseModalFooter.jsx";
import {
  Dialog,
  DialogPortal
} from "@/modern/components/ui/dialog";
import { cn } from "@/modern/utils";
import { useEffect, useRef, useMemo, useState } from "react";
import { getFontSize } from "@/react/context/bryntumContext.jsx";
import { getBryntumFloatRootElement } from "@/other/utils/domUtils.jsx";

const isEditorInFront = (modalElement, containerElement) => {
  return modalElement === containerElement.lastElementChild;
}

const parseSizeToPixels = (sizeValue) => {
  if (!sizeValue) return 0;
  if (typeof sizeValue === 'number') return sizeValue;
  if (sizeValue.endsWith('rem')) {
    const rem = parseFloat(sizeValue);
    return rem * getFontSize(); // Assuming 1rem = 16px
  }
  if (sizeValue.endsWith('px')) {
    return parseFloat(sizeValue);
  }
  if (sizeValue.endsWith('vh')) {
    const vh = parseFloat(sizeValue);
    return (vh / 100) * window.innerHeight;
  }
  if (sizeValue.endsWith('vw')) {
    const vw = parseFloat(sizeValue);
    return (vw / 100) * window.innerWidth;
  }
  return 0;
};

/**
 * @typedef {Object} BaseModalSize
 * @property {string} [minWidth]
 * @property {string} [minHeight]
 * @property {string} [width]
 * @property {string} [height]
 * @property {string} [maxWidth]
 * @property {string} [maxHeight]
 */

/**
 * @typedef {Object} BaseModalProps
 * @property {any} record - The record associated with the modal
 * @property {React.ReactNode} children - Modal content
 * @property {() => void} onClose - Function to call when modal closes
 * @property {string} title - Modal title
 * @property {string} description - Modal description
 * @property {BaseModalSize} [size] - Modal size configuration
 * @property {string} [className] - Additional CSS classes
 * @property {(() => React.ReactNode) | undefined | null} [footerRenderer] - Custom footer renderer function
 * @property {(() => React.ReactNode) | undefined | null} [headerRenderer] - Custom header renderer function
 * @property {string} [headerIconCls] - Header icon class
 * @property {boolean} [modal] - Whether to show overlay
 * @property {Object} [contextMenuConfig] - Context menu configuration
 * @property {any} [version] - Version information
 * @property {any} [swivuAssistanceConfig] - Swivu assistance configuration
 * @property {boolean} [hideMenu] - Whether to hide menu
 * @property {any} editorInstance - Editor instance reference
 * @property {string} [headerClassName] - Additional header CSS classes
 * @property {Object} [defaultPosition] - Default position of the modal
 * @property {string} [footerClassName] - Additional footer CSS classes
 * @property {(event: Event) => void} [onInteractOutside] - Radix UI's built-in handler for clicks/interactions outside the modal (respects modal nesting)
 * @property {string} [zIndex] - Custom z-index value (e.g., "z-[150000]" or "z-50"). Defaults to "z-[--swivu-bryntum-z-index]"
 * @property {React.CSSProperties} [style] - Additional inline styles applied to the modal content element
 * @property {string} [dragHandleSelector] - Selector for the drag handle element
 */

/**
 * Base modal component for editors
 * @param {BaseModalProps} props
 */
export const BaseModal = ({
  record,
  children,
  onClose,
  title,
  description,
  size = { minWidth: "300px", minHeight: "300px", width: "300px", height: "300px", maxWidth: "100vw", maxHeight: "100vh" },
  className = "",
  footerRenderer,
  headerRenderer,
  headerIconCls,
  modal,
  contextMenuConfig = {},
  version,
  swivuAssistanceConfig,
  hideMenu = false,
  editorInstance,
  headerClassName = "",
  defaultPosition = undefined,
  footerClassName = "",
  onInteractOutside = null,
  zIndex = "",
  style = {},
  dragHandleSelector = ".drag-handle"
}) => {
  const { minWidth, minHeight, width, height, maxWidth, maxHeight } = size;
  // const container = document.querySelector(".b-float-root");
  const container = useMemo(() => getBryntumFloatRootElement(), []);
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const dragNodeRef = useRef(null);

  // Constrain position to keep modal within viewport
  const constrainedPosition = useMemo(() => {
    if (!defaultPosition || defaultPosition.x === undefined || defaultPosition.y === undefined) {
      return defaultPosition;
    }

    const modalWidth = parseSizeToPixels(width);
    const modalHeight = parseSizeToPixels(height);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let { x, y } = defaultPosition;

    // Ensure modal doesn't go beyond right edge
    if (x + modalWidth > viewportWidth) {
      x = Math.max(0, viewportWidth - modalWidth);
    }

    // Ensure modal doesn't go beyond bottom edge
    if (y + modalHeight > viewportHeight) {
      y = Math.max(0, viewportHeight - modalHeight);
    }

    // Ensure modal doesn't go beyond left edge
    x = Math.max(0, x);

    // Ensure modal doesn't go beyond top edge
    y = Math.max(0, y);

    return { x, y };
  }, [defaultPosition, width, height]);

  const resetPosition = constrainedPosition ?? { x: 0, y: 0 };
  const [dragPosition, setDragPosition] = useState(resetPosition);

  // Re-centre whenever zoom changes (apply transform directly — avoid remounting Draggable)
  useEffect(() => {
    setDragPosition(resetPosition);
    const node = dragNodeRef.current;
    if (node) {
      node.style.transform = `translate(${resetPosition.x}px, ${resetPosition.y}px)`;
    }
  }, [style.zoom, resetPosition.x, resetPosition.y]);

  useEffect(() => {
    if (!editorInstance) throw new Error("editorInstance is required, please provide 'editorInstance' to the BaseModal");

    editorInstance.editorRef = {
      toFront: () => {
        if (modalRef.current && container) {
          const isInFront = isEditorInFront(modalRef.current, container);
          if (isInFront) return;
          const widgetFragment = document.createDocumentFragment();
          if (overlayRef.current) widgetFragment.append(overlayRef.current);
          widgetFragment.append(modalRef.current);
          container.append(widgetFragment);
        }
      }
    }
  }, [editorInstance, modalRef, container]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        const isInFront = isEditorInFront(modalRef.current, container);
        if (isInFront) onClose();
      };
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, modalRef, container])

  // Handle click outside using Radix UI's layer system
  useEffect(() => {
    // Note: had to implement a custom solution outside click close as Radix's built-in onInteractOutside does not work
    if (!onInteractOutside) return;

    const handleClickOutside = (e) => {
      if (!modalRef.current || !container) return;

      // Check if this modal is the frontmost one
      const isInFront = isEditorInFront(modalRef.current, container);
      if (!isInFront) return;

      // Find the .base-modal element
      const baseModalElement = modalRef.current.querySelector('.base-modal');
      if (!baseModalElement) return;

      // Check if click is outside the modal content
      if (!baseModalElement.contains(e.target)) {
        onInteractOutside(e);
      }
    };

    // Small delay to prevent immediate close on mount
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handleClickOutside, true);
    }, 150);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', handleClickOutside, true);
    };
  }, [onInteractOutside, modalRef, container]);

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal container={container}>
        {modal && <div ref={overlayRef} className="fixed inset-0 bg-black/80 pointer-events-auto" />}
        <div ref={modalRef} className={cn("absolute inset-0 pointer-events-none", zIndex, !constrainedPosition ? "flex items-center justify-center z-1" : "")}>
          <Draggable
            nodeRef={dragNodeRef}
            handle={dragHandleSelector}
            cancel="input, textarea, button, select, option, [data-widget-close]"
            bounds="parent"
            onStart={() => editorInstance.editorRef.toFront()}
            defaultPosition={dragPosition}
          >
            <div
              ref={dragNodeRef}
              className={cn(`base-modal bg-card text-card-foreground rounded-[var(--swivu-base-border-radius)_var(--swivu-base-border-radius)_2px_2px] shadow-2xl overflow-hidden border border-border border-solid flex flex-col resize pointer-events-auto`, className)}
              style={{ minWidth, minHeight, width, height, maxWidth, maxHeight, ...style }}>
              {headerRenderer ? (
                headerRenderer()
              ) : (
                <BaseModalHeader
                  titleCls={cn(record.archived && "line-through")}
                  title={title}
                  description={description}
                  onClose={onClose}
                  record={record}
                  iconCls={headerIconCls}
                  contextMenuConfig={contextMenuConfig}
                  version={version}
                  swivuAssistanceConfig={swivuAssistanceConfig}
                  hideMenu={hideMenu}
                  onClick={() => editorInstance.editorRef.toFront()}
                  className={headerClassName}
                />
              )}
              {children}
              {footerRenderer && <BaseModalFooter className={footerClassName}>{footerRenderer()}</BaseModalFooter>}
            </div>
          </Draggable>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
